
import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { WorldCity } from '@/data/countries/types';

interface CitiesListProps {
  cities: WorldCity[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  countryName: string;
}

const CitiesList = ({ cities, totalPages, currentPage, onPageChange, countryName }: CitiesListProps) => {
  // Generate pagination range
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (cities.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-brand-gray-600 text-lg">
          No cities found matching your search criteria.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cities.map((city) => (
          <Link 
            key={city.slug}
            to={`/city/${city.slug}`}
            className="bg-white rounded-lg shadow-sm border border-brand-gray-200 p-4 hover:shadow-md transition-all duration-300 flex items-center justify-between group"
          >
            <div className="flex items-center">
              <MapPin className="h-4 w-4 text-brand-blue-500 mr-3 flex-shrink-0" />
              <div>
                <span className="text-brand-gray-800 font-medium block">
                  Therapists, Psychologists & Psychotherapists in {city.name}
                </span>
                <span className="text-xs text-brand-gray-500">
                  {city.population ? `${Math.round(city.population / 100000) / 10}M residents` : 'Major city'}
                </span>
              </div>
            </div>
            <div className="text-brand-gray-400 group-hover:text-brand-blue-500 transition-colors">
              →
            </div>
          </Link>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <Pagination>
            <PaginationContent>
              {currentPage > 1 && (
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => onPageChange(currentPage - 1)}
                    className="cursor-pointer hover:bg-brand-gray-100"
                  />
                </PaginationItem>
              )}
              
              {getPaginationRange().map((pageNum, index) => {
                if (pageNum === '...') {
                  return (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink 
                      onClick={() => onPageChange(pageNum as number)}
                      isActive={currentPage === pageNum}
                      className="cursor-pointer hover:bg-brand-gray-100"
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              
              {currentPage < totalPages && (
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => onPageChange(currentPage + 1)}
                    className="cursor-pointer hover:bg-brand-gray-100"
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <div className="mt-12 p-6 bg-brand-gray-50 rounded-xl text-center">
        <h3 className="text-xl font-semibold text-brand-gray-900 mb-4">
          Can't find your city?
        </h3>
        <p className="text-brand-gray-700 mb-4">
          We're constantly expanding our network. You can still find qualified therapists, psychologists, and psychotherapists 
          through our online platform regardless of your location.
        </p>
        <Button asChild>
          <Link to="/find">Find Therapists, Psychologists & Psychotherapists Online</Link>
        </Button>
      </div>
    </>
  );
};

export default CitiesList;
